import CatalogCategories from "@/pages/configuration/delay-categories";
import { BarrelsPage } from "@/pages/configuration/barrels";
import { PassTypesPage } from "@/pages/configuration/pass-types";
import { PassIdsPage } from "@/pages/configuration/pass-ids";
import { CartridgesPage } from "@/pages/configuration/cartridges";
import { PassChangeReasonsPage } from "@/pages/configuration/pass-change-reasons";
import { RollMaterialsPage } from "@/pages/configuration/roll-materials";
import { RollApplicationsPage } from "@/pages/configuration/roll-applications";
import { RollsBlankPage } from "@/pages/configuration/rolls-blank";
import { CostCentersPage } from "@/pages/configuration/cost-centers";
import { OperatorsPage } from "@/pages/configuration/operators";
import { SuppliersPage } from "@/pages/configuration/suppliers";
import { ProductTypesPage } from "@/pages/configuration/product-types";
import { ProductsPage } from "@/pages/configuration/products";
import { ProductChangesPage } from "@/pages/configuration/product-changes";
import { RecipesPage } from "@/pages/configuration/recipes";
import { StraightenerLimitsPage } from "@/pages/configuration/straightener-limits";
import {
	PERMISSION_STRAIGHTENER_LIMITS,
	PERMISSION_STRAIGHTENER_RECIPES,
} from "@/pages/production/straightener-common/permissions";
import { StraightenerRecipesPage } from "@/pages/production/straightener-recipes";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import FormatListNumberedOutlinedIcon from "@mui/icons-material/FormatListNumberedOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LinearScaleOutlinedIcon from "@mui/icons-material/LinearScaleOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import TripOriginOutlinedIcon from "@mui/icons-material/TripOriginOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import {
	type ConfigSidebarSection,
	type ConfigurationsType,
	useHasPermission,
} from "@dexteel/mesf-core";
import type { ReactNode } from "react";
import CatalogDelayCodes from "../pages/configuration/delay-codes";

type Component = {
	path: string;
	sidebar: () => ReactNode;
	main: () => ReactNode;
};

const components: Component[] = [
	// R-Factor components
	{
		path: "barrels",
		sidebar: () => <div>Barrels</div>,
		main: () => <BarrelsPage />,
	},
	{
		path: "pass-types",
		sidebar: () => <div>Pass Types</div>,
		main: () => <PassTypesPage />,
	},
	{
		path: "pass-ids",
		sidebar: () => <div>Pass IDs</div>,
		main: () => <PassIdsPage />,
	},
	// Delays components
	{
		path: "delay-categories",
		sidebar: () => <div>Categories</div>,
		main: () => <CatalogCategories />,
	},
	{
		path: "delay-codes",
		sidebar: () => <div>Codes</div>,
		main: () => <CatalogDelayCodes delayAreaAssetName="RBD Mill" />,
	},
	{
		path: "pass-change-reasons",
		sidebar: () => <div>Pass Change Reasons</div>,
		main: () => <PassChangeReasonsPage />,
	},
	{
		path: "cartridges",
		sidebar: () => <div>Cartridges</div>,
		main: () => <CartridgesPage />,
	},
	{
		path: "roll-materials",
		sidebar: () => <div>Roll Materials</div>,
		main: () => <RollMaterialsPage />,
	},
	{
		path: "roll-applications",
		sidebar: () => <div>Roll Applications</div>,
		main: () => <RollApplicationsPage />,
	},
	{
		path: "rolls-blank",
		sidebar: () => <div>Rolls Blank</div>,
		main: () => <RollsBlankPage />,
	},
	{
		path: "cost-centers",
		sidebar: () => <div>Cost Centers</div>,
		main: () => <CostCentersPage />,
	},
	{
		path: "operators",
		sidebar: () => <div>Operators</div>,
		main: () => <OperatorsPage />,
	},
	{
		path: "suppliers",
		sidebar: () => <div>Suppliers</div>,
		main: () => <SuppliersPage />,
	},
	{
		path: "product-types",
		sidebar: () => <div>Product Types</div>,
		main: () => <ProductTypesPage />,
	},
	{
		path: "products",
		sidebar: () => <div>Products</div>,
		main: () => <ProductsPage />,
	},
	{
		path: "product-changes",
		sidebar: () => <div>Product Changes</div>,
		main: () => <ProductChangesPage />,
	},
	{
		path: "recipes",
		sidebar: () => <div>Recipes</div>,
		main: () => <RecipesPage />,
	},
	{
		path: "straightener-limits",
		sidebar: () => <div>Parameters</div>,
		main: () => <StraightenerLimitsPage />,
	},
	{
		path: "straightener-recipes",
		sidebar: () => <div>Recipes</div>,
		main: () => <StraightenerRecipesPage />,
	},
];

const sidebarSections: ConfigSidebarSection[] = [
	{
		name: "Mill",
		icon: <PrecisionManufacturingOutlinedIcon />,
		items: [
			{
				path: "barrels",
				label: "Barrels",
				icon: <StraightenOutlinedIcon />,
			},
			{
				path: "cartridges",
				label: "Cartridges",
				icon: <ViewModuleOutlinedIcon />,
			},
			{
				path: "operators",
				label: "Operators",
				icon: <BadgeOutlinedIcon />,
			},
			{
				path: "pass-change-reasons",
				label: "Pass Change Reasons",
				icon: <SwapHorizOutlinedIcon />,
			},
			{
				path: "pass-ids",
				label: "Pass IDs",
				icon: <FormatListNumberedOutlinedIcon />,
			},
			{
				path: "pass-types",
				label: "Pass Types",
				icon: <TuneOutlinedIcon />,
			},
			{
				path: "product-changes",
				label: "Product Changes",
				icon: <CompareArrowsOutlinedIcon />,
			},
			{
				path: "product-types",
				label: "Product Types",
				icon: <LabelOutlinedIcon />,
			},
			{
				path: "products",
				label: "Products",
				icon: <InventoryOutlinedIcon />,
			},
			{
				path: "recipes",
				label: "Recipes",
				icon: <MenuBookOutlinedIcon />,
			},
		],
	},
	{
		name: "Straightener",
		icon: <LinearScaleOutlinedIcon />,
		items: [
			{
				path: "straightener-limits",
				label: "Parameters",
				icon: <TimelineOutlinedIcon />,
			},
			{
				path: "straightener-recipes",
				label: "Recipes",
				icon: <MenuBookOutlinedIcon />,
			},
		],
	},
	{
		name: "Rollshop",
		icon: <TripOriginOutlinedIcon />,
		items: [
			{
				path: "cost-centers",
				label: "Cost Centers",
				icon: <AccountBalanceOutlinedIcon />,
			},
			{
				path: "roll-applications",
				label: "Roll Applications",
				icon: <AppsOutlinedIcon />,
			},
			{
				path: "roll-materials",
				label: "Roll Materials",
				icon: <ScienceOutlinedIcon />,
			},
			{
				path: "rolls-blank",
				label: "Rolls Blank",
				icon: <CircleOutlinedIcon />,
			},
			{
				path: "suppliers",
				label: "Suppliers",
				icon: <LocalShippingOutlinedIcon />,
			},
		],
	},
	{
		name: "Stops",
		icon: <PauseCircleOutlinedIcon />,
		items: [
			{
				path: "delay-categories",
				label: "Categories",
				icon: <CategoryOutlinedIcon />,
			},
			{
				path: "delay-codes",
				label: "Codes",
				icon: <TagOutlinedIcon />,
			},
		],
	},
];

const itemPermissions: Record<string, string> = {
	"straightener-limits": PERMISSION_STRAIGHTENER_LIMITS,
	"straightener-recipes": PERMISSION_STRAIGHTENER_RECIPES,
};

const useSidebarSections = (): ConfigSidebarSection[] => {
	const hasPermissionTo = useHasPermission();

	return sidebarSections
		.map((section) => ({
			...section,
			items: section.items.filter((item) => {
				const permission = itemPermissions[item.path];
				return !permission || hasPermissionTo(permission);
			}),
		}))
		.filter((section) => section.items.length > 0);
};

const customConfigurations: ConfigurationsType = [
	components,
	null,
	useSidebarSections,
];

export { customConfigurations };
